/* Wishlist Hero SDK の読み込み待機とレスポンス正規化を共有する */
(() => {
  if (window.AuremeWishlistSdk) return;

  const WISHLIST_SDK_READY_EVENT = 'wishlist-hero-wishlist-sdk-ready';
  const WISHLIST_SDK_WAIT_TIMEOUT_MS = 10000;
  const DEFAULT_SERVER_FALLBACK_WARNING =
    '[aureme-wishlist-sdk] Wishlist Hero SDK のサーバー取得に失敗したためキャッシュを参照します。';
  const DEFAULT_VARIANT_TITLE = 'Default Title';
  const PRODUCT_VARIANT_TITLE_SEPARATOR = ' - ';
  const TOAST_RETRY_INTERVAL_MS = 100;
  const TOAST_MAX_RETRY_COUNT = 20;
  const WISHLIST_ADD_SUCCESS_MSG = 'マイページに追加されました。';
  const WISHLIST_REMOVE_SUCCESS_MSG = 'お気に入りを解除しました。';
  const WISHLIST_ADD_ERROR_MSG =
    'お気に入りの登録に失敗しました。\n時間をおいて再度お試しください。';
  const WISHLIST_REMOVE_ERROR_MSG =
    'お気に入りの解除に失敗しました。\n時間をおいて再度お試しください。';
  const WISHLIST_ADD_METHOD_NAME = 'AddWishListItem';
  const WISHLIST_DELETE_METHOD_NAME = 'DeleteWishListItem';
  const SHOPIFY_PRODUCT_VARIANT_GID_PREFIX = 'gid://shopify/ProductVariant/';

  function normalizeText(value) {
    return String(value || '').trim();
  }

  function normalizeWishlistOptionText(value) {
    const optionText = normalizeText(value);
    return optionText && optionText !== DEFAULT_VARIANT_TITLE ? optionText : '';
  }

  function buildWishlistProductTitle(productTitle, productOptionText) {
    const normalizedProductTitle = normalizeText(productTitle);
    const normalizedOptionText = normalizeWishlistOptionText(productOptionText);
    if (!normalizedOptionText) return normalizedProductTitle;

    return `${normalizedProductTitle}${PRODUCT_VARIANT_TITLE_SEPARATOR}${normalizedOptionText}`;
  }

  /*
   * Wishlist Hero へ渡す商品名は「商品名 - variant 名」形式のため、
   * 既知の optionText がある場合はその suffix だけを剥がし、商品名内の区切り文字を誤分割しない。
   */
  function splitWishlistProductTitle(productName, { expectedOptionText = '' } = {}) {
    const normalizedProductName = normalizeText(productName);
    const normalizedExpectedOptionText = normalizeWishlistOptionText(expectedOptionText);

    if (normalizedExpectedOptionText) {
      const expectedSuffix = `${PRODUCT_VARIANT_TITLE_SEPARATOR}${normalizedExpectedOptionText}`;
      if (normalizedProductName.endsWith(expectedSuffix)) {
        const title = normalizeText(normalizedProductName.slice(0, -expectedSuffix.length));
        if (title) {
          return { optionText: normalizedExpectedOptionText, title };
        }
      }

      return { optionText: normalizedExpectedOptionText, title: normalizedProductName };
    }

    const separatorIndex = normalizedProductName.lastIndexOf(PRODUCT_VARIANT_TITLE_SEPARATOR);
    if (separatorIndex <= 0) {
      return { optionText: '', title: normalizedProductName };
    }

    const title = normalizeText(normalizedProductName.slice(0, separatorIndex));
    const optionText = normalizeWishlistOptionText(
      normalizedProductName.slice(separatorIndex + PRODUCT_VARIANT_TITLE_SEPARATOR.length),
    );
    if (!title || !optionText) {
      return { optionText: '', title: normalizedProductName };
    }

    return { optionText, title };
  }

  function normalizeId(value) {
    const id = normalizeText(value);
    return id.startsWith(SHOPIFY_PRODUCT_VARIANT_GID_PREFIX)
      ? id.slice(SHOPIFY_PRODUCT_VARIANT_GID_PREFIX.length)
      : id;
  }

  function normalizeSdkId(value) {
    const id = normalizeId(value);
    return Number(id) || id;
  }

  function getRootUrlPrefix() {
    const rootUrl = normalizeText(window.Aureme?.config?.rootUrl) || '/';
    return rootUrl.replace(/\/+$/, '');
  }

  function getProductPathSegments(pathname) {
    const pathSegments = String(pathname || '')
      .split('/')
      .filter(Boolean);
    const productsIndex = pathSegments.indexOf('products');
    if (productsIndex < 0 || !pathSegments[productsIndex + 1]) return [];

    return pathSegments.slice(productsIndex);
  }

  function getProductHandleFromPathname(pathname) {
    const productPathSegments = getProductPathSegments(pathname);
    return normalizeProductHandle(productPathSegments[1]);
  }

  function normalizeProductHandle(productHandle) {
    const normalizedHandle = normalizeText(productHandle);
    if (!normalizedHandle) return '';

    try {
      return decodeURIComponent(normalizedHandle);
    } catch {
      return normalizedHandle;
    }
  }

  function buildRootedProductPathname(pathname) {
    const productPathSegments = getProductPathSegments(pathname);
    if (productPathSegments.length === 0) return '';

    const productPathname = `/${productPathSegments.join('/')}`;
    return `${getRootUrlPrefix()}${productPathname}`;
  }

  function buildProductUrl(productHandle, { productVariantId = '' } = {}) {
    const normalizedHandle = normalizeText(productHandle);
    if (!normalizedHandle) return '';

    const normalizedProductVariantId = normalizeText(productVariantId);
    const variantQuery = normalizedProductVariantId
      ? `?variant=${encodeURIComponent(normalizedProductVariantId)}`
      : '';

    return `${getRootUrlPrefix()}/products/${encodeURIComponent(normalizedHandle)}${variantQuery}`;
  }

  function getWishlistSdk() {
    return window.WishListHero_SDK || null;
  }

  function waitForWishlistSdk({ timeoutMs = WISHLIST_SDK_WAIT_TIMEOUT_MS } = {}) {
    const existingSdk = getWishlistSdk();
    if (existingSdk) return Promise.resolve(existingSdk);

    return new Promise((resolve, reject) => {
      let timeoutId;

      const cleanup = () => {
        document.removeEventListener(WISHLIST_SDK_READY_EVENT, handleSdkReady);
        window.clearTimeout(timeoutId);
      };

      const handleSdkReady = () => {
        const sdk = getWishlistSdk();
        if (!sdk) return;

        cleanup();
        resolve(sdk);
      };

      timeoutId = window.setTimeout(() => {
        cleanup();
        reject(new Error('Wishlist Hero SDK is not loaded.'));
      }, timeoutMs);

      document.addEventListener(WISHLIST_SDK_READY_EVENT, handleSdkReady);
      /*
       * listener 登録直前〜直後の ready event 取りこぼしを避けるため、
       * 登録直後に SDK の現在状態をもう一度確認する。
       */
      handleSdkReady();
    });
  }

  /*
   * Wishlist Hero SDK は取得経路やバージョンで配列の包み方が揺れるため、
   * 呼び出し側へ渡す前に item 配列だけへ正規化する。
   */
  function normalizeWishlistItems(response) {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.wishlist)) return response.wishlist;
    if (Array.isArray(response?.items)) return response.items;
    return [];
  }

  async function fetchWishlistItems(
    sdk,
    { preferServer = false, serverFallbackWarning = DEFAULT_SERVER_FALLBACK_WARNING } = {},
  ) {
    /*
     * 呼び出し元ごとにサーバー優先 / SDK キャッシュ優先を切り替える。
     * サーバー取得が落ちても通常取得 API があれば、表示や操作を止めずに既存キャッシュで継続する。
     */
    if (preferServer && typeof sdk.GetWishListItemsFromServer === 'function') {
      try {
        return normalizeWishlistItems(await sdk.GetWishListItemsFromServer());
      } catch {
        if (typeof sdk.GetWishListItems !== 'function') {
          throw new Error('Wishlist Hero SDK server fetch failed.');
        }

        if (serverFallbackWarning) console.warn(serverFallbackWarning);
      }
    }

    if (typeof sdk.GetWishListItems === 'function') {
      return normalizeWishlistItems(await sdk.GetWishListItems());
    }

    if (typeof sdk.GetWishListItemsFromServer === 'function') {
      return normalizeWishlistItems(await sdk.GetWishListItemsFromServer());
    }

    throw new Error('Wishlist Hero SDK does not provide wishlist item APIs.');
  }

  async function fetchWishlistHash(sdk, { createIfMissing = false } = {}) {
    if (typeof sdk.GetWishListHash !== 'function') return '';
    return String((await sdk.GetWishListHash(createIfMissing)) || '');
  }

  function createWishlistHashError() {
    const error = new Error('Wishlist Hero SDK wishlist hash is missing.');
    error.wishlistHashMissing = true;
    return error;
  }

  async function fetchRequiredWishlistHash(sdk, options = {}) {
    const wishlistHash = await fetchWishlistHash(sdk, options);
    if (!wishlistHash) throw createWishlistHashError();

    return wishlistHash;
  }

  /*
   * 削除 API で必須の wishlistHash を mutation context 内で保持する。
   * mutation 後は通常取得とサーバー取得を同じ検証契約で扱う。
   */
  function createWishlistMutationContext(
    sdk,
    { serverFallbackWarning = DEFAULT_SERVER_FALLBACK_WARNING } = {},
  ) {
    let wishlistHash = '';

    function getFetchOptions(preferServer) {
      return {
        preferServer,
        serverFallbackWarning,
      };
    }

    async function getRequiredHash({ createIfMissing = false } = {}) {
      if (wishlistHash) return wishlistHash;

      wishlistHash = await fetchRequiredWishlistHash(sdk, { createIfMissing });
      return wishlistHash;
    }

    async function mutateItem({
      createIfMissing = false,
      fetchWishlistItemsOptions = getFetchOptions(false),
      retryFetchWishlistItemsOptions = getFetchOptions(true),
      wishlistHash: providedWishlistHash = '',
      ...mutationOptions
    }) {
      const resolvedWishlistHash =
        providedWishlistHash || (await getRequiredHash({ createIfMissing }));

      return mutateWishlistItem(sdk, {
        fetchWishlistItemsOptions,
        retryFetchWishlistItemsOptions,
        wishlistHash: resolvedWishlistHash,
        ...mutationOptions,
      });
    }

    return {
      fetchItems({ preferServer = false } = {}) {
        return fetchWishlistItems(sdk, getFetchOptions(preferServer));
      },
      getRequiredHash,
      mutateItem,
    };
  }

  function getWishlistMutationMethodName(isCurrentlyAdded) {
    return isCurrentlyAdded ? WISHLIST_DELETE_METHOD_NAME : WISHLIST_ADD_METHOD_NAME;
  }

  /*
   * SDK item は handle を直接持たない場合があるため、保存済みの商品 URL からも
   * Shopify の product handle を復元する。
   */
  function getWishlistItemProductHandle(item) {
    const explicitHandle = normalizeProductHandle(
      item?.ProductHandle ?? item?.Handle ?? item?.handle,
    );
    if (explicitHandle) return explicitHandle;

    const productUrl = normalizeText(item?.ProductLink ?? item?.ProductUrl ?? item?.url);
    if (!productUrl) return '';

    try {
      const url = new URL(productUrl, window.location.origin);
      return getProductHandleFromPathname(url.pathname);
    } catch {
      return '';
    }
  }

  function getWishlistItemVariantId(item) {
    return normalizeId(
      item?.ProductVariantId ?? item?.VariantId ?? item?.VariantID ?? item?.variantId,
    );
  }

  function buildWishlistItemsByVariantId(wishlistItems) {
    const wishlistItemsByVariantId = new Map();
    if (!Array.isArray(wishlistItems)) return wishlistItemsByVariantId;

    for (const item of wishlistItems) {
      const productVariantId = getWishlistItemVariantId(item);
      if (productVariantId) wishlistItemsByVariantId.set(productVariantId, item);
    }

    return wishlistItemsByVariantId;
  }

  function buildWishlistVariantIdSet(wishlistItems) {
    return new Set(buildWishlistItemsByVariantId(wishlistItems).keys());
  }

  function wishlistItemsIncludeVariant(wishlistItems, productVariantId) {
    const normalizedProductVariantId = normalizeId(productVariantId);
    if (!normalizedProductVariantId) return false;

    return wishlistItems.some(
      (wishlistItem) => getWishlistItemVariantId(wishlistItem) === normalizedProductVariantId,
    );
  }

  async function addWishlistItem(sdk, productInfo) {
    const addMethod = sdk?.[WISHLIST_ADD_METHOD_NAME];
    if (typeof addMethod !== 'function') {
      throw new Error('Wishlist Hero SDK does not provide add API.');
    }

    await addMethod.call(sdk, productInfo);
  }

  async function deleteWishlistItem(sdk, { productVariantId, wishlistHash }) {
    const deleteMethod = sdk?.[WISHLIST_DELETE_METHOD_NAME];
    if (typeof deleteMethod !== 'function') {
      throw new Error('Wishlist Hero SDK does not provide delete API.');
    }
    if (!wishlistHash) throw createWishlistHashError();

    await deleteMethod.call(sdk, {
      ProductVariantId: normalizeSdkId(productVariantId),
      WishlistHash: wishlistHash,
    });
  }

  /*
   * SDK の通常取得はキャッシュが先に返る場合があるため、
   * 期待状態とずれたときだけサーバー取得で再確認する。
   */
  async function verifyWishlistItemState(
    sdk,
    {
      expectedIsAdded,
      fetchWishlistItemsOptions = {},
      productVariantId,
      retryFetchWishlistItemsOptions = { preferServer: true },
    },
  ) {
    let wishlistItems = await fetchWishlistItems(sdk, {
      preferServer: false,
      ...fetchWishlistItemsOptions,
    });
    let isAdded = wishlistItemsIncludeVariant(wishlistItems, productVariantId);

    if (isAdded !== expectedIsAdded) {
      wishlistItems = await fetchWishlistItems(sdk, {
        preferServer: true,
        ...retryFetchWishlistItemsOptions,
      });
      isAdded = wishlistItemsIncludeVariant(wishlistItems, productVariantId);
    }

    return {
      isAdded,
      wishlistItems,
    };
  }

  async function mutateWishlistItem(
    sdk,
    {
      fetchWishlistItemsOptions,
      isCurrentlyAdded,
      productInfo = null,
      productVariantId,
      retryFetchWishlistItemsOptions,
      wishlistHash,
    },
  ) {
    const expectedIsAdded = !isCurrentlyAdded;

    if (isCurrentlyAdded) {
      await deleteWishlistItem(sdk, { productVariantId, wishlistHash });
    } else {
      await addWishlistItem(sdk, productInfo);
    }

    const mutationResult = await verifyWishlistItemState(sdk, {
      expectedIsAdded,
      fetchWishlistItemsOptions,
      productVariantId,
      retryFetchWishlistItemsOptions,
    });

    if (mutationResult.isAdded !== expectedIsAdded) {
      throw new Error('Wishlist Hero SDK mutation did not update wishlist state.');
    }

    return mutationResult;
  }

  function getWishlistSuccessToastMessage(isAdded) {
    return isAdded ? WISHLIST_ADD_SUCCESS_MSG : WISHLIST_REMOVE_SUCCESS_MSG;
  }

  function getWishlistErrorToastMessage(wasAdded) {
    return wasAdded ? WISHLIST_REMOVE_ERROR_MSG : WISHLIST_ADD_ERROR_MSG;
  }

  function showAuremeToast(message, type) {
    if (window.AuremeToast?.instance) {
      window.AuremeToast.show({
        message,
        type,
      });
      return true;
    }

    return false;
  }

  function showToast(message, type = 'error') {
    if (!message) return;
    if (showAuremeToast(message, type)) return;
    /*
     * Wishlist 系 script は toast より先に実行されることがあるため、
     * 初期化完了を短時間だけ待って同じ共通トーストへ集約する。
     */
    const createReadyRetryScheduler = window.AuremeReadyRetry?.createScheduler;
    if (typeof createReadyRetryScheduler !== 'function') {
      console.error('[aureme-wishlist-sdk] ready retry helper is not loaded.');
      return;
    }

    const toastRetryScheduler = createReadyRetryScheduler({
      intervalMs: TOAST_RETRY_INTERVAL_MS,
      maxRetryCount: TOAST_MAX_RETRY_COUNT,
    });

    function scheduleToast(retryCount = 0) {
      toastRetryScheduler.schedule({
        tryReady: () => showAuremeToast(message, type),
        onRetry: scheduleToast,
        retryCount,
      });
    }

    scheduleToast();
  }

  function showErrorToast(message) {
    showToast(message, 'error');
  }

  window.AuremeWishlistSdk = {
    buildProductUrl,
    buildRootedProductPathname,
    buildWishlistItemsByVariantId,
    buildWishlistVariantIdSet,
    buildWishlistProductTitle,
    createWishlistMutationContext,
    fetchWishlistHash,
    fetchWishlistItems,
    fetchRequiredWishlistHash,
    getWishlistItemVariantId,
    getWishlistItemProductHandle,
    getWishlistErrorToastMessage,
    getWishlistSdk,
    getWishlistMutationMethodName,
    getWishlistSuccessToastMessage,
    mutateWishlistItem,
    normalizeId,
    normalizeSdkId,
    normalizeText,
    normalizeWishlistOptionText,
    showErrorToast,
    showToast,
    splitWishlistProductTitle,
    waitForWishlistSdk,
    wishlistAddMethodName: WISHLIST_ADD_METHOD_NAME,
    wishlistDeleteMethodName: WISHLIST_DELETE_METHOD_NAME,
    wishlistItemsIncludeVariant,
  };
})();
